import { useEffect, useRef, useState, useMemo } from 'react'
import { apiGet } from 'tennders-stracture/libs/api'

const useAutocomplete = (props) => {
  const {
    dataExtractor,
    url,
    requestBuilder,
    items,
    onSet,
    value,
    customValue,
    initialValue
  } = props
  const getUrl = (value) => `${url}?query=${value}`
  const localDataExtractor = (data) => data.map((o) => ({
    label: o.label,
    id: o.id
  }))
  const localRequestBuilder = ({ keyword, options }) => apiGet(getUrl(keyword), options, true)
  const extractor = dataExtractor || localDataExtractor
  const builder = requestBuilder || localRequestBuilder
  const [suggestions, setSuggestions] = useState(items)
  const [showNoDataPrompt, setShowNoDataPrompt] = useState(true)
  const [suggestionsLoading, setSuggestionsLoading] = useState(true)
  const [text, setText] = useState<string | number | undefined>(initialValue?.label || '')
  const controller = useRef(null)

  const handleSuggestionSelect = (item) => {
    setText(item.label?.replace(/(<([^>]+)>)/gi, '') || '')
    setSuggestions([])
    setShowNoDataPrompt(false)
    if (onSet) {
      onSet(item)
    }
  }

  const onTextChanged = async (e) => {
    setSuggestionsLoading(true)
    const _value = e?.target?.value
    setText(_value)
    if (items) {
      const filtered = [...items].filter((item) => item.label.includes(_value) || item.label.includes(_value))
      setSuggestions(filtered)
    } else if (_value.length > 0) {
      if (controller.current) {
        controller.current.abort()
      }
      controller.current = new AbortController()
      const response = await builder({
        keyword: _value,
        options: { signal: controller.current.signal }
      })
      if (response) {
        controller.current = null
        setSuggestions(extractor(response))
      }
      setShowNoDataPrompt(true)
    } else {
      setSuggestions([])
    }
    if (_value === '') {
      onSet(null)
    }
    setTimeout(() => {
      setSuggestionsLoading(false)
    }, 1000)
  }

  const showNoDataText = useMemo(() => text && !suggestionsLoading && showNoDataPrompt, [text, suggestionsLoading, showNoDataPrompt])

  useEffect(() => {
    if (value?.label) {
      setText(value.label)
    } else if (typeof value === 'string' && value) {
      setText(value)
    } else if (customValue && !initialValue?.label) {
      onTextChanged({ target: { value: customValue } })
    } else if (initialValue?.label) {
      setText(initialValue?.label)
    } else {
      setText('')
    }
  }, [value, initialValue, customValue])

  useEffect(() => {
    setSuggestions(items)
  }, [items])

  return {
    text,
    suggestions,
    setSuggestions,
    onTextChanged,
    suggestionsLoading,
    showNoDataPrompt,
    handleSuggestionSelect,
    showNoDataText
  }
}

export default useAutocomplete
